import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { checkoutApi, tradeInApi } from '../lib/api';
import { CreditCard, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import PayPalCheckout from '../components/PayPalCheckout';

export default function CheckoutPage() {
  const { cart, refreshCart, loading: cartLoading, initialized: cartInitialized } = useCart();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { t, tField } = useLang();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [tradeInDiscount, setTradeInDiscount] = useState(0);

  // Check for returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderId = searchParams.get('order_id');

    if (sessionId) {
      pollPaymentStatus(sessionId, orderId);
    }
  }, [searchParams]);

  // Load approved (unused) Biolosophy Value discount
  useEffect(() => {
    const fetchApprovedDiscount = async () => {
      try {
        const res = await tradeInApi.getHistory();
        const approved = (res.data.trade_ins || []).filter(
          (t) => t.status === 'approved' && !t.used
        );
        if (approved.length > 0) {
          setTradeInDiscount(Math.max(...approved.map((t) => t.discount_percentage || 0)));
        }
      } catch (error) {
        console.error('Error fetching approved discounts:', error);
      }
    };
    if (isAuthenticated) fetchApprovedDiscount();
  }, [isAuthenticated]);

  const pollPaymentStatus = async (sessionId, orderId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast({
        title: 'Timeout',
        description: 'Verifica lo stato del pagamento nel tuo account',
        variant: 'destructive',
      });
      navigate(`/account/orders`);
      return;
    }

    try {
      const response = await checkoutApi.getStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        toast({
          title: 'Pagamento completato!',
          description: 'Grazie per il tuo ordine',
        });
        await refreshCart();
        navigate(`/checkout/success?order_id=${orderId}`);
        return;
      } else if (data.status === 'expired') {
        toast({
          title: 'Sessione scaduta',
          description: 'Riprova il pagamento',
          variant: 'destructive',
        });
        navigate('/cart');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, orderId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, orderId, attempts + 1), pollInterval);
    }
  };

  // Redirect guards (run after auth/cart have finished loading)
  useEffect(() => {
    if (searchParams.get('session_id')) return;
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (cartInitialized && !cartLoading && cart.items.length === 0) {
      navigate('/cart');
    }
  }, [authLoading, isAuthenticated, cartInitialized, cartLoading, cart.items.length, searchParams, navigate]);

  if (authLoading || (isAuthenticated && !cartInitialized && !searchParams.get('session_id'))) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A5D23]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (cart.items.length === 0 && !searchParams.get('session_id')) {
    return null;
  }

  const shipping = cart.total >= 300 ? 0 : 6.90;
  const discountAmount = (cart.total * tradeInDiscount) / 100;
  const finalTotal = cart.total - discountAmount + shipping;

  const handleCheckout = async () => {
    setProcessing(true);

    try {
      const response = await checkoutApi.create({
        origin_url: window.location.origin,
        payment_method: paymentMethod,
        trade_in_discount: tradeInDiscount,
      });

      if (paymentMethod === 'stripe') {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile procedere al pagamento',
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  const handlePayPalSuccess = async (orderId) => {
    toast({
      title: t({ it: 'Pagamento completato!', en: 'Payment completed!' }),
      description: t({ it: 'Grazie per il tuo ordine', en: 'Thank you for your order' }),
    });
    await refreshCart();
    navigate(`/checkout/success?order_id=${orderId}`);
  };

  const handlePayPalError = () => {
    toast({
      title: t({ it: 'Pagamento non riuscito', en: 'Payment failed' }),
      description: t({ it: 'Il pagamento PayPal non è andato a buon fine. Riprova.', en: 'The PayPal payment was not completed. Please try again.' }),
      variant: 'destructive',
    });
  };

  // If we're processing a return from Stripe
  if (searchParams.get('session_id')) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin mx-auto text-[#4A5D23] mb-4" />
          <p className="text-lg">Verifica pagamento in corso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/cart"
          data-testid="back-to-cart-link"
          className="inline-flex items-center gap-2 text-[#57534E] hover:text-[#1C1917] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t({ it: 'Torna al Carrello', en: 'Back to Cart' })}
        </Link>

        <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-8">
          Checkout
        </h1>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Customer Info */}
            <div className="bg-[#F2EFE9] p-6">
              <h2 className="text-xl font-medium mb-4">{t({ it: 'Informazioni Cliente', en: 'Customer Information' })}</h2>
              <div className="space-y-2 text-[#57534E]">
                <p><strong>{t({ it: 'Nome', en: 'Name' })}:</strong> {user?.name}</p>
                <p><strong>Email:</strong> {user?.email}</p>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-[#F2EFE9] p-6">
              <h2 className="text-xl font-medium mb-4">{t({ it: 'Metodo di Pagamento', en: 'Payment Method' })}</h2>
              <div className="space-y-4">
                <label
                  className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${
                    paymentMethod === 'stripe'
                      ? 'border-[#4A5D23] bg-white'
                      : 'border-[#E5E0D8] hover:border-[#1C1917]'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="stripe"
                    checked={paymentMethod === 'stripe'}
                    onChange={() => setPaymentMethod('stripe')}
                    data-testid="payment-stripe"
                    className="w-5 h-5 accent-[#4A5D23]"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      <span className="font-medium">{t({ it: 'Carta di Credito/Debito', en: 'Credit/Debit Card' })}</span>
                    </div>
                    <p className="text-sm text-[#57534E] mt-1">
                      Visa, Mastercard, American Express
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${
                    paymentMethod === 'paypal'
                      ? 'border-[#4A5D23] bg-white'
                      : 'border-[#E5E0D8] hover:border-[#1C1917]'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={() => setPaymentMethod('paypal')}
                    data-testid="payment-paypal"
                    className="w-5 h-5 accent-[#4A5D23]"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .757-.63h6.153c2.046 0 3.542.474 4.448 1.41.906.936 1.2 2.184.875 3.713l-.017.085c-.002.01-.004.02-.004.03-.34 1.733-1.1 3.073-2.258 3.976-1.13.88-2.62 1.326-4.432 1.326H8.196l-.95 5.93a.771.771 0 0 1-.76.632h-1.41v1.145H7.076zm.93-7.17l.99-6.194h2.026c1.168 0 2.03.225 2.57.67.538.447.718 1.123.536 2.012-.206 1.006-.658 1.74-1.347 2.184-.691.446-1.65.668-2.85.668H8.006z" />
                      </svg>
                      <span className="font-medium">PayPal</span>
                    </div>
                    <p className="text-sm text-[#57534E] mt-1">
                      {t({ it: 'Paga in sicurezza con il tuo account PayPal', en: 'Pay securely with your PayPal account' })}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Trade-in Discount */}
            {tradeInDiscount > 0 && (
              <div data-testid="trade-in-discount-box" className="bg-[#4A5D23]/10 p-6 border border-[#4A5D23]">
                <h2 className="text-xl font-medium mb-2 text-[#4A5D23]">{t({ it: 'Sconto Biolosophy Value Applicato', en: 'Biolosophy Value Discount Applied' })}</h2>
                <p className="text-[#57534E]">
                  {t({
                    it: `Hai diritto a uno sconto del ${tradeInDiscount}% grazie al Biolosophy Value (reso approvato)!`,
                    en: `You are entitled to a ${tradeInDiscount}% discount thanks to Biolosophy Value (approved return)!`,
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#F2EFE9] p-6 sticky top-24">
              <h2 className="text-xl font-medium mb-6">{t({ it: 'Riepilogo Ordine', en: 'Order Summary' })}</h2>
              
              <div className="space-y-4 mb-6">
                {cart.items.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <img
                      src={item.product?.images?.[0] || 'https://via.placeholder.com/60x80'}
                      alt={item.product?.name}
                      className="w-16 h-20 object-cover"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{tField(item.product, 'name')}</p>
                      <p className="text-xs text-[#57534E]">{item.size} / {item.color}</p>
                      <p className="text-xs text-[#57534E]">{t({ it: 'Qtà', en: 'Qty' })}: {item.quantity}</p>
                    </div>
                    <p className="font-medium">€{item.item_total?.toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-3 border-t border-[#E5E0D8] pt-4 mb-6">
                <div className="flex justify-between text-[#57534E]">
                  <span>{t({ it: 'Subtotale', en: 'Subtotal' })}</span>
                  <span>€{cart.total.toFixed(2)}</span>
                </div>
                {tradeInDiscount > 0 && (
                  <div data-testid="summary-trade-in-discount" className="flex justify-between text-[#4A5D23]">
                    <span>{t({ it: 'Sconto Biolosophy Value', en: 'Biolosophy Value Discount' })} ({tradeInDiscount}%)</span>
                    <span>-€{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#57534E]">
                  <span>{t({ it: 'Spedizione', en: 'Shipping' })}</span>
                  <span>{shipping === 0 ? t({ it: 'Gratuita', en: 'Free' }) : `€${shipping.toFixed(2)}`}</span>
                </div>
                <div className="border-t border-[#E5E0D8] pt-3">
                  <div className="flex justify-between text-lg font-medium">
                    <span>{t({ it: 'Totale', en: 'Total' })}</span>
                    <span>€{finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {paymentMethod === 'paypal' ? (
                <PayPalCheckout onSuccess={handlePayPalSuccess} onError={handlePayPalError} />
              ) : (
                <button
                  onClick={handleCheckout}
                  disabled={processing}
                  data-testid="complete-checkout-btn"
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t({ it: 'Elaborazione...', en: 'Processing...' })}
                    </>
                  ) : (
                    <>
                      {t({ it: 'Completa Ordine', en: 'Complete Order' })}
                      <Check className="w-5 h-5" />
                    </>
                  )}
                </button>
              )}

              <p className="text-xs text-[#57534E] text-center mt-4">
                {t({
                  it: 'Cliccando "Completa Ordine" accetti i nostri Termini e Condizioni',
                  en: 'By clicking "Complete Order" you accept our Terms and Conditions',
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
